import React from 'react';
import { Space, Tag, Typography } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { formatMoney } from '@/utils/format';
import { epcEstimationService } from '@/services/epcEstimation.service';

const { Text } = Typography;

export interface EpcEstimateBoqCoverageStripProps {
  estimateId: string;
  revisionId: string;
  currency?: string;
  onFilterUnlinked?: () => void;
  onFilterBoqWithoutCost?: () => void;
}

/**
 * EST3 UX3 Wave 1 — BOQ ↔ cost coverage ribbon (always visible under status strip).
 */
export const EpcEstimateBoqCoverageStrip: React.FC<EpcEstimateBoqCoverageStripProps> = ({
  estimateId,
  revisionId,
  currency,
  onFilterUnlinked,
  onFilterBoqWithoutCost,
}) => {
  const covQ = useQuery({
    queryKey: ['epc-est-boq-coverage', estimateId, revisionId],
    queryFn: () => epcEstimationService.boqCostCoverage(estimateId, revisionId),
    enabled: !!estimateId && !!revisionId,
    staleTime: 30_000,
  });
  const d = covQ.data;
  if (!d && covQ.isLoading) return null;
  if (!d) return null;

  const linkedPct = Number(d.pct_costs_linked ?? 0);
  const boqPct = Number(d.pct_boq_with_cost ?? 0);
  const linkedColor = linkedPct >= 90 ? 'green' : linkedPct >= 70 ? 'orange' : 'red';
  const boqColor = boqPct >= 90 ? 'green' : boqPct >= 70 ? 'orange' : 'red';

  return (
    <Space wrap size={[12, 8]} style={{ padding: '4px 0 8px' }}>
      <Text type="secondary">BOQ coverage:</Text>
      <Tag color={linkedColor}>{linkedPct}% costs linked</Tag>
      <Tag color={boqColor}>{boqPct}% BOQ with cost</Tag>
      {Number(d.unlinked_cost_line_count) > 0 ? (
        <Tag
          color="orange"
          style={{ cursor: onFilterUnlinked ? 'pointer' : undefined }}
          onClick={onFilterUnlinked}
        >
          {d.unlinked_cost_line_count} unlinked ·{' '}
          {formatMoney(d.unallocated_cost_amount, {
            display: 'plain',
            maximumFractionDigits: 0,
          })}
          {currency ? ` ${currency}` : ''}
        </Tag>
      ) : (
        <Tag color="green">All costs linked</Tag>
      )}
      {Number(d.boq_without_cost_count) > 0 ? (
        <Tag
          color="default"
          style={{ cursor: onFilterBoqWithoutCost ? 'pointer' : undefined }}
          onClick={onFilterBoqWithoutCost}
        >
          {d.boq_without_cost_count} BOQ without cost
        </Tag>
      ) : null}
      {Number(d.orphan_cost_line_count) > 0 ? (
        <Tag color="red">{d.orphan_cost_line_count} orphan cost ref(s)</Tag>
      ) : null}
    </Space>
  );
};
